// Battle report tab.
//
// Read-only by design: it generates reports and never changes the adventure.
// The newest report is expanded and older ones are collapsed, because the
// value of keeping them is comparison, and comparison needs the previous one
// visible without a click.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';

interface StoredReport {
  id: string;
  generated_at: string;
  kind: 'daily' | 'adhoc';
  verdict: 'complete' | 'won' | 'drifting' | 'at_risk' | 'unrecoverable';
  headline: string;
  body: string;
  generated_by: string;
}

// Verdict colouring. Same semantics as everywhere else: gold is good or
// finished, crimson is the alarm. Nothing here invents a new meaning.
const VERDICT_STYLE: Record<StoredReport['verdict'], string> = {
  complete: 'text-gold border-amber-400/50 bg-[rgba(251,191,36,0.10)]',
  won: 'text-gold border-amber-400/40 bg-[rgba(251,191,36,0.07)]',
  drifting: 'text-ink border-white/15 bg-white/5',
  at_risk: 'text-accent-danger border-rose-400/40 bg-rose-500/5',
  unrecoverable: 'text-accent-danger border-rose-500/60 bg-rose-500/10',
};

const VERDICT_WORD: Record<StoredReport['verdict'], string> = {
  complete: 'complete',
  won: 'goals met',
  drifting: 'drifting',
  at_risk: 'at risk',
  unrecoverable: 'out of reach',
};

function when(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export function BattleReportTab({ adventureId }: { adventureId: string }) {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);

  const reports = useQuery({
    queryKey: ['battle-reports', adventureId],
    queryFn: async () => {
      const res = await api<{ reports: StoredReport[] }>(`/api/admin/adventures/${adventureId}/battle-reports`);
      return res.reports;
    },
  });

  const generate = useMutation({
    mutationFn: async () => {
      const res = await api<{ report: StoredReport }>(`/api/admin/adventures/${adventureId}/battle-reports`, {
        method: 'POST',
      });
      return res.report;
    },
    onSuccess: (report) => {
      setOpenId(report.id);
      void qc.invalidateQueries({ queryKey: ['battle-reports', adventureId] });
    },
  });

  const list = reports.data ?? [];
  const newest = list[0];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-ink">Battle report</h2>
          <p className="text-sm text-ink-muted mt-1">
            A snapshot of where this adventure stands. Written every morning at 08:00, and on demand.
          </p>
        </div>
        <button
          type="button"
          className="btn-primary shrink-0"
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
        >
          {generate.isPending ? 'Generating…' : 'Generate now'}
        </button>
      </div>

      {generate.isError && (
        <p className="text-sm text-accent-danger">Could not generate a report. Try again in a moment.</p>
      )}

      {reports.isLoading && <p className="text-sm text-ink-muted">Loading…</p>}

      {!reports.isLoading && list.length === 0 && (
        <div className="card p-6 text-sm text-ink-muted">
          No reports yet. The first daily report appears at 08:00, or press <span className="text-ink">Generate now</span>.
        </div>
      )}

      {newest && (
        <div className={`rounded border px-4 py-3 ${VERDICT_STYLE[newest.verdict]}`}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs uppercase tracking-wide opacity-80">
              {VERDICT_WORD[newest.verdict]}
            </span>
            <span className="text-xs opacity-60">{when(newest.generated_at)}</span>
          </div>
          <p className="mt-1 font-medium">{newest.headline}</p>
        </div>
      )}

      <ul className="space-y-2">
        {list.map((r) => {
          const open = openId === r.id || (openId === null && r.id === newest?.id);
          return (
            <li key={r.id} className="bg-bg-elev border border-border rounded">
              <button
                type="button"
                className="w-full text-left px-4 py-3 flex items-baseline justify-between gap-3"
                onClick={() => setOpenId(open ? '' : r.id)}
                aria-expanded={open}
              >
                <span className="truncate text-sm text-ink">{when(r.generated_at)}</span>
                <span className="shrink-0 text-xs text-ink-muted">
                  {r.kind === 'daily' ? 'daily' : 'ad hoc'} · {VERDICT_WORD[r.verdict]}
                </span>
              </button>
              {open && (
                <pre className="px-4 pb-4 text-xs whitespace-pre-wrap font-mono text-ink-muted">
                  {r.body}
                </pre>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
